import { Button } from "@/components/ui/shadcn/Button/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/shadcn/Popover/Popover";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import type { Connection, Matter, MatterContactConnection } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getConnectionPreviewContact } from "@/lib/utils/connections/item-contact-connections";
import {
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import { getPrimaryItem } from "@/lib/utils/matterContactConnectionHelpers";
import { ContactEmailIcon, ContactPhoneIcon } from "@/lib/utils/iconUtils";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { Typography } from "../Typography/Typography";

export interface CompactContactPreviewChipProps {
  contact: MatterContactConnection | Connection;
  matter?: Matter;
  className?: string;
  textClassName?: string;
  buttonClassName?: string;
}

export const CompactContactPreviewChip = ({
  contact,
  matter,
  className,
  textClassName,
  buttonClassName,
}: CompactContactPreviewChipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { openCommunicationModal } = useGlobalModal();
  const isMatterContactConnection = "contactId" in contact;
  const embeddedContact = contact.contact;
  const preview = isMatterContactConnection
    ? undefined
    : getConnectionPreviewContact(contact);
  const contactName =
    formatContactsName(embeddedContact ?? undefined) || "Unknown contact";
  const contactEmail = isMatterContactConnection
    ? contact.contact.emails[0]?.address
    : getPrimaryItem(preview?.emails ?? [], "isPrimary")?.address;
  const contactPhone = isMatterContactConnection
    ? contact.contact.phones.at(0)
    : getPrimaryItem(preview?.phones ?? [], "isPrimary");
  const contactId = isMatterContactConnection
    ? contact.contactId
    : contact.targetObjectId;
  return (
    <div className={cn("flex items-center gap-x-1", className)}>
      <Typography className={textClassName}>{contactName}</Typography>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="link"
            className={cn(
              "text-muted-foreground h-4 w-4 p-0 focus-visible:ring-0 focus-visible:ring-offset-0",
              buttonClassName,
            )}
            size="icon"
            onClick={() => {
              setIsOpen(!isOpen);
            }}
          >
            <ChevronRight className={cn("h-4 w-4", isOpen && "rotate-90")} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex w-fit flex-col items-start gap-y-1 p-2">
          <Typography size="sm" className="flex items-center gap-x-1">
            <ContactEmailIcon className="h-4 w-4" />
            {contactEmail ? (
              contactEmail
            ) : (
              <Typography variant="subHeading">No email address</Typography>
            )}
          </Typography>
          <Button
            variant="link"
            size="sm"
            className="h-5 p-0"
            onClick={() => {
              setIsOpen(false);
              openCommunicationModal({
                contactId,
                matterId: matter?.id,
              });
            }}
          >
            <Typography size="sm" className="flex items-center gap-x-1">
              <ContactPhoneIcon className="h-4 w-4" />
              {contactPhone?.number ? (
                formatLocalOrIntlPhoneNumber(contactPhone)
              ) : (
                <Typography variant="subHeading">No phone number</Typography>
              )}
            </Typography>
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  );
};
